import { useEffect, useRef } from 'react';
import { createWorkspace, listWorkspaces, getWorkspace, updateWorkspace } from '../api/client.js';
import { useAppStore } from './store.js';

// Backend persistence for the auto-saved workspace.
//
// Today: localStorage via zustand `persist` keeps the layout in-browser.
// This component layers backend sync on top so the same layout follows
// the user across devices.
//
// Strategy:
//   - On mount: GET /workspaces, find the row named "__default" (our
//     reserved auto-save slot), GET its layoutJson, apply to store.
//     If no row exists yet, create one from the current local store.
//   - On store change: debounced PUT /workspaces/:id with the latest
//     persisted slice. Open-dev still works (backend's preHandler
//     treats requests as dev-user when Clerk keys are absent).
//
// We don't replace localStorage — it stays as a hot cache that boots
// instantly while the backend round-trips on its own timeline.

const DEFAULT_NAME = '__default';
const PUSH_DEBOUNCE_MS = 1200;

// Panel-component keys that have been removed from the catalogue.
// Server-side workspace snapshots can still reference them — strip on
// pull so a re-login doesn't restore phantom panels (Landing tab kept
// re-showing "Demand-side shifts" after every login because this sync
// overwrote the local store, bypassing the store's persist-migration).
const REMOVED_COMPONENTS = new Set(['landing-demand', 'rejected-list', 'landing-buyer']);

interface PersistSlice {
  tabs: unknown;
  tabOrder: unknown;
  activeTabId: unknown;
  watchlist: unknown;
  filters: unknown;
}

function snapshot(): PersistSlice {
  const s = useAppStore.getState();
  return {
    tabs: s.tabs,
    tabOrder: s.tabOrder,
    activeTabId: s.activeTabId,
    watchlist: s.watchlist,
    filters: s.filters,
  };
}

/** Remove any tab-scoped panel records whose `component` is no longer
 *  registered, and drop the cached dockview layout for any tab we
 *  touched (so it re-seeds cleanly from the surviving panels on next
 *  render). Mutates in place — returns the same object. */
function stripDeadPanels(layout: Record<string, unknown>): Record<string, unknown> {
  const tabs = layout['tabs'];
  if (!tabs || typeof tabs !== 'object') return layout;
  for (const tab of Object.values(tabs as Record<string, Record<string, unknown>>)) {
    const panels = tab['panels'] as Record<string, { component?: string } | undefined> | undefined;
    if (!panels) continue;
    let dirty = false;
    for (const [panelId, panel] of Object.entries(panels)) {
      if (panel && REMOVED_COMPONENTS.has(panel.component ?? '')) {
        delete panels[panelId];
        dirty = true;
      }
    }
    if (dirty) delete tab['dockviewLayout'];
  }
  return layout;
}

export function BackendSync() {
  const workspaceIdRef = useRef<string | null>(null);
  const pendingTimer = useRef<number | null>(null);
  const initialPullDone = useRef(false);

  // One-shot pull on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listWorkspaces();
        const row = list.data.find((r) => r.name === DEFAULT_NAME);
        if (cancelled) return;
        if (row) {
          workspaceIdRef.current = row.id;
          const detail = await getWorkspace(row.id);
          if (cancelled) return;
          const layout = detail.data.layoutJson;
          // Merge backend layout over local one. Skipping on missing
          // tabs prevents wiping the local store if the backend was
          // seeded empty by an earlier bug.
          if (layout && typeof layout === 'object' && 'tabs' in layout) {
            const cleaned = stripDeadPanels(layout as Record<string, unknown>);
            // Ghost-filter guard: the workspace market dropdown was
            // removed from the header, so a server-persisted
            // filters.activeCountry (e.g. 'NO' from before the removal)
            // would re-apply a market filter the user can no longer
            // clear — silently hiding every market-gated panel. Force it
            // null on pull; the next debounced push clears it server-side.
            const f = cleaned['filters'];
            if (f && typeof f === 'object') {
              (f as Record<string, unknown>)['activeCountry'] = null;
            }
            useAppStore.setState(cleaned as Partial<typeof useAppStore.getState>);
          }
        } else {
          // First time — create the default slot from whatever's in
          // localStorage right now.
          const created = await createWorkspace({
            name: DEFAULT_NAME,
            description: 'Auto-saved layout',
            layoutJson: snapshot() as unknown as Record<string, unknown>,
            isDefault: true,
          });
          workspaceIdRef.current = created.data.id;
        }
      } catch (err) {
        console.warn('BackendSync: initial pull failed; staying on local cache', err);
      } finally {
        initialPullDone.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounced push on every store change.
  useEffect(() => {
    return useAppStore.subscribe(() => {
      if (!initialPullDone.current) return;
      const id = workspaceIdRef.current;
      if (!id) return;
      if (pendingTimer.current !== null) window.clearTimeout(pendingTimer.current);
      pendingTimer.current = window.setTimeout(() => {
        void updateWorkspace(id, {
          layoutJson: snapshot() as unknown as Record<string, unknown>,
        }).catch((err) => {
          console.warn('BackendSync: push failed; local cache still ahead', err);
        });
      }, PUSH_DEBOUNCE_MS);
    });
  }, []);

  return null;
}
